import { makeAction } from "async-action-creator";
import { notification } from "antd";
import * as R from "ramda";

import i18n from "../../i18n";
import api from "../../utils/api";
import { createApiThunk } from "../../utils/thunk";

import { logout } from "./session";
import {
  // fetchCountries,
  fetchProfessions,
  fetchStudies,
  fetchSkills
} from "./catalogs";

export const FETCH_USER_PROFILE = makeAction("user/FETCH_PROFILE");
export const FETCH_USER_CALENDARS = makeAction("user/FETCH_CALENDARS");
export const SELECT_USER_CALENDAR = makeAction("user/SELECT_CALENDAR");
export const UPDATE_USER_PROFILE = makeAction("user/UPDATE_PROFILE");
export const UPDATE_USER_PASSWORD = makeAction("user/UPDATE_PASSWORD");
export const UPDATE_USER_PREFERENCES = makeAction("user/UPDATE_PREFERENCES");
export const CONNECT_USER_SOCIAL_ACCOUNT = makeAction(
  "company/CONNECT_USER_SOCIAL_ACCOUNT"
);
export const DISCONNECT_USER_SOCIAL_ACCOUNT = makeAction(
  "company/DISCONNECT_USER_SOCIAL_ACCOUNT"
);

export const updateUserProfile = createApiThunk({
  action: UPDATE_USER_PROFILE,
  request: async (formData, { company, user }, dispatch) => {
    const profile = api.user.update({
      ...formData,
      user: user.id,
      company: company.id
    });
    await Promise.all([
      dispatch(fetchProfessions(company.country)),
      dispatch(fetchSkills({ country: company.country })),
      // dispatch(fetchCountries()),
      dispatch(fetchStudies())
    ]);

    return profile;
  },
  onResolved: ({ payload }) => {
    const locale = R.prop("locale", payload);

    if (locale) {
      i18n.changeLanguage(locale);
    }

    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:user.updateProfileResolved")
    });
  },
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:user.updateProfileRejected")
    })
});

export const fetchUserCalendars = createApiThunk({
  action: FETCH_USER_CALENDARS,
  request: (formData, { company, user }) =>
    api.user.update({
      ...formData,
      user: user.id,
      company: company.id
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:user.fetchUserCalendarsRejected")
    })
});

export const selectUserCalendar = createApiThunk({
  action: SELECT_USER_CALENDAR,
  request: (formData, { company, user }) =>
    api.user.update({
      ...formData,
      user: user.id,
      company: company.id
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:user.selectUserCalendarRejected")
    })
});

export const updateUserPassword = createApiThunk({
  action: UPDATE_USER_PASSWORD,
  request: (values, { user, company }) =>
    api.user.changePassword({
      companyId: company.id,
      userId: user.id,
      email: user.email,
      password: values["current-password"],
      newPassword: values["new-password"]
    }),
  onResolved: ({ dispatch }) => {
    setTimeout(() => {
      dispatch(logout());
    }, 2000);

    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:user.updatePasswordResolved")
    });
  },
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:user.updatePasswordRejected")
    })
});

export const fetchUserProfile = createApiThunk({
  action: FETCH_USER_PROFILE,
  request: async (company, { company: companyData, headquarter, user }) => {
    if (!company) {
      throw new Error("Company id is required to fetch the user profile");
    }

    const profile = await api.user.fetch({
      company,
      user: user.id
    });

    const locale =
      profile.locale === ""
        ? companyData.country || headquarter.country
        : profile.locale;

    if (locale) {
      i18n.changeLanguage(locale);
    }

    return profile;
  }
});

export const updateUserPreferences = createApiThunk({
  action: UPDATE_USER_PREFERENCES,
  request: async (formData, { user, company }) => {
    const profile = await api.user.update({
      company: company.id,
      user: user.id,
      ...formData
    });

    if (formData.locale) {
      i18n.changeLanguage(formData.locale);
    }

    return profile;
  },
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:user.updatePreferencesResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:user.updatePreferencesRejected")
    })
});

export const connectUserSocialAccount = createApiThunk({
  action: CONNECT_USER_SOCIAL_ACCOUNT,
  request: ({ provider, ...options }, { company, user }) =>
    api.user.social.create(company.id, user.id, provider, options),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:company.updateCompanySocialAccountsResolved")
    }),
  onRejected: error => {
    const status = R.path(["response", "status"], error);
    return notification.error({
      message: i18n.t("global:error"),
      description:
        status === 400
          ? i18n.t("toasts:company.updateCompanySocialAccountsExists")
          : i18n.t("toasts:company.updateCompanySocialAccountsRejected")
    });
  }
});

export const disconnectUserSocialAccount = createApiThunk({
  action: DISCONNECT_USER_SOCIAL_ACCOUNT,
  request: (provider, { company, user }) =>
    api.user.social.delete(company.id, user.id, provider),
  onResolved: () =>
    notification.success({
      message: i18n.t("global:good"),
      description: i18n.t("toasts:company.updateCompanySocialAccountsResolved")
    }),
  onRejected: () =>
    notification.error({
      message: i18n.t("global:error"),
      description: i18n.t("toasts:company.updateCompanySocialAccountsRejected")
    })
});
